import {
  getFilteredSubmissionIdsByAnswerSearch,
  getFormattedFiltersBasedOnFormSchema,
  getFormSubmissionQueryPayloadWithAnswerFilter,
} from "#src/batteries-included-components/Panels/TablePanels/FormSubmissionsTablePanel.helpers";
import { useBulkExportFormSubmissionAsPDF } from "#src/components/Forms/useExportFormSubmissionAsPDF";
import { Brandmark } from "#src/components/Presentational/Logos/Brandmark";
import { useTableSortingAndPagination } from "#src/components/Redux/reducers/tableStateReducer";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useParams, useSearchParams } from "#src/Routers/hooks";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import {
  Button,
  LoadingState,
  Page,
  ProgressBar,
  toast,
} from "@validereinc/common-components";
import {
  FormCategoryAdapter,
  FormSchemaAdapter,
  FormSubmissionAdapter,
  FormSubmissionGetListRequestType,
  FormSubmissionStatus,
  Resources,
  SortDirection,
} from "@validereinc/domain";
import { toFlattenedObject } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { cloneDeep, isEmpty } from "es-toolkit/compat";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Titled } from "react-titled";
import { FormSubmissionsDownloadPDFsRoute } from ".";
import styles from "./FormSubmissionsDownloadPDFsPage.module.scss";

const cx = classNames.bind(styles);

const PAGE_SIZE = 500;

export const FormSubmissionsDownloadPDFsPage = () => {
  const [{ "submission-type": submissionsType }] = useSearchParams();
  const isDraft = submissionsType === "draft";
  const [downloadUrl, setDownloadUrl] = useState<string | undefined>(undefined);
  const [pdfProgress, setPdfProgress] = useState(0);
  const { categoryId, formTemplateId } = useParams<{
    categoryId: string;
    formTemplateId: string;
  }>();
  const downloadedRef = useRef<boolean>(false);
  const startTimeRef = useRef<number>(Date.now());

  const [filterConfigStorageKey, viewConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "view-configuration", "table-state"],
      ["forms", "templates", formTemplateId, "submissions"]
    );
  const [selectionStorageKey] = useStorageKey(
    ["download-selection"],
    "form-submissions-table"
  );

  const [viewFilters] = useStickyState<
    FormSubmissionGetListRequestType["filters"]
  >({}, viewConfigStorageKey);
  const [tableFilters] = useStickyState<
    FormSubmissionGetListRequestType["filters"]
  >({}, filterConfigStorageKey);
  const [downloadSelection] = useStickyState<{
    form_submission_ids: string[];
  }>({ form_submission_ids: [] }, selectionStorageKey);

  //freeze the filters on initial load so they can't be changed by the user on a different page and cause a redownload
  const [filters] = useState<any>(() => ({
    ...tableFilters,
    ...viewFilters,
  }));
  const [selectedFormSubmissions] = useState<string[]>(() => {
    return downloadSelection.form_submission_ids;
  });

  const categoryQuery = useQuery({
    queryKey: [Resources.FORM_CATEGORY, categoryId],
    queryFn: async ({ queryKey }) => {
      const [_, id] = queryKey;

      return await FormCategoryAdapter.getOne({
        id,
      });
    },
    enabled: !!categoryId,
  });

  const templateQuery = useQuery({
    queryKey: [Resources.FORM_SCHEMA, formTemplateId],
    queryFn: async ({ queryKey }) => {
      const [_, formSchemaId] = queryKey;

      return await FormSchemaAdapter.getOne({
        id: formSchemaId,
      });
    },
    staleTime: 2 * 60 * 1000,
    select: (resp) => resp?.data,
    enabled: !!formTemplateId,
  });

  const [breadcrumbs] = useBreadcrumbsFromRoute(
    FormSubmissionsDownloadPDFsRoute,
    {
      ":formTemplateId": {
        title: templateQuery?.data?.name,
      },
      ":categoryId": {
        title: categoryQuery?.data?.name,
      },
    }
  );

  const {
    created_at,
    status,
    created_by,
    dataset_submission_id,
    ...restFilters
  } = filters;
  const [{ sortBy, sortDirection, ...pagination }] =
    useTableSortingAndPagination(
      {
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      filters,
      tableStateStorageKey
    );
  const schemaQuery = useQuery({
    queryKey: [Resources.FORM_SCHEMA, formTemplateId],
    queryFn: () => {
      if (!formTemplateId) return;

      return FormSchemaAdapter.getOne({
        id: formTemplateId,
      });
    },
    enabled: Boolean(formTemplateId),
    select: (resp) => resp?.data,
    staleTime: 2 * 60 * 1000,
  });
  const formattedAnswerFilters = useMemo(
    () =>
      schemaQuery.data
        ? getFormattedFiltersBasedOnFormSchema(restFilters, schemaQuery.data)
        : null,
    [restFilters, schemaQuery.data]
  );

  const hasFormattedAnswerFilters = !isEmpty(formattedAnswerFilters);
  const answersQueryFilter = {
    created_at,
    created_by,
    dataset_submission_id,
    ...toFlattenedObject({
      status: isDraft
        ? FormSubmissionStatus.DRAFT
        : status
          ? status
          : Object.values(FormSubmissionStatus).filter(
              (s) => s !== FormSubmissionStatus.DRAFT
            ),
    }),
    ...formattedAnswerFilters,
  };

  const {
    data: answersQuery,
    hasNextPage: answersQueryHasNextPage,
    isFetching: answersQueryIsFetching,
    isLoading: answersQueryIsLoading,
    fetchNextPage: answersQueryFetchNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "forms",
      "submissions",
      "answers",
      formTemplateId,
      answersQueryFilter,
    ],
    queryFn: ({ pageParam }) => {
      if (!formTemplateId || !hasFormattedAnswerFilters) return;

      return FormSubmissionAdapter.answers.getListV2({
        page: pageParam ?? 1,
        pageSize: PAGE_SIZE,
        sortBy,
        sortDirection,
        filters: answersQueryFilter as any,
        meta: {
          form_schema_id: formTemplateId,
        },
      });
    },
    getNextPageParam: (resp) => {
      return resp
        ? resp.page_number < resp.total_pages
          ? resp.page_number + 1
          : undefined
        : undefined;
    },
    enabled: Boolean(formTemplateId) && hasFormattedAnswerFilters,
    staleTime: 2 * 60 * 1000,
  });

  const filteredSubmissionIdsByAnswerSearch = useMemo(
    () => getFilteredSubmissionIdsByAnswerSearch(answersQuery),
    [answersQuery?.pages]
  );

  const queryPayloadWithoutAnswerFilter: Parameters<
    typeof FormSubmissionAdapter.getList
  >[0] = {
    page: pagination.page,
    pageSize: PAGE_SIZE,
    sortBy,
    sortDirection,
    filters: {
      created_by,
      created_at,
      dataset_submission_id,
      ...toFlattenedObject({
        status: isDraft
          ? FormSubmissionStatus.DRAFT
          : status
            ? status
            : Object.values(FormSubmissionStatus).filter(
                (s) => s !== FormSubmissionStatus.DRAFT
              ),
        "form_schema.status": "active",
      }),
      ...(formTemplateId ? { "form_schema.id": formTemplateId } : {}),
    },
    meta: { answers: true },
  };

  const queryPayload = useMemo(() => {
    const filters = cloneDeep(
      getFormSubmissionQueryPayloadWithAnswerFilter(
        queryPayloadWithoutAnswerFilter,
        hasFormattedAnswerFilters,
        filteredSubmissionIdsByAnswerSearch
      )
    );
    if (selectedFormSubmissions.length > 0) {
      filters.filters = {
        id: { $in: selectedFormSubmissions },
        isAlreadyFormatted: true,
      };
    }
    return filters;
  }, [
    selectedFormSubmissions,
    queryPayloadWithoutAnswerFilter,
    hasFormattedAnswerFilters,
    filteredSubmissionIdsByAnswerSearch,
  ]);

  const initialFormSubmissionsQuery = useQuery({
    queryKey: [Resources.FORM_SUBMISSION, queryPayload],
    queryFn: () => {
      if (
        !formTemplateId ||
        (hasFormattedAnswerFilters &&
          (answersQueryIsLoading || answersQueryIsFetching))
      )
        return;

      return FormSubmissionAdapter.getList(queryPayload);
    },
    enabled:
      Boolean(formTemplateId) &&
      (!hasFormattedAnswerFilters ||
        (!answersQueryIsLoading && !answersQueryIsFetching)),
    staleTime: 2 * 60 * 1000,
  });

  const totalNumberItems = initialFormSubmissionsQuery.data?.total_entries ?? 1;

  const remainingFormSubmissionsQueries = useQueries({
    queries: Array(Math.ceil(totalNumberItems / PAGE_SIZE) - 1)
      .fill(0)
      .map((_, idx) => ({
        queryKey: [
          Resources.FORM_SUBMISSION,
          { ...cloneDeep(queryPayload), page: idx + 2 },
        ],
        queryFn: () => {
          const updatedQueryPayload = {
            ...cloneDeep(queryPayload),
            page: idx + 2,
          };
          return FormSubmissionAdapter.getList(updatedQueryPayload);
        },
        enabled:
          Boolean(formTemplateId) &&
          !initialFormSubmissionsQuery.isLoading &&
          (!hasFormattedAnswerFilters ||
            (!answersQueryIsLoading && !answersQueryIsFetching)),
        staleTime: 2 * 60 * 1000,
      })),
  });

  const isFetchingFormSubmissions = useMemo(
    () =>
      initialFormSubmissionsQuery.isLoading ||
      (initialFormSubmissionsQuery.data &&
        initialFormSubmissionsQuery.data.total_entries > PAGE_SIZE &&
        remainingFormSubmissionsQueries.some((q) => q.isLoading)),
    [initialFormSubmissionsQuery.isLoading, remainingFormSubmissionsQueries]
  );
  const allFormSubmissions = useMemo(
    () =>
      initialFormSubmissionsQuery.data?.data &&
      remainingFormSubmissionsQueries.every((q) => q.data?.data)
        ? [
            initialFormSubmissionsQuery.data.data,
            ...remainingFormSubmissionsQueries.map((q) => q.data!.data),
          ].flat()
        : [],
    [
      initialFormSubmissionsQuery.data?.data,
      remainingFormSubmissionsQueries.every((q) => q.data?.data),
    ]
  );

  const bulkExportPDFMutation = useBulkExportFormSubmissionAsPDF();

  useEffect(() => {
    if (answersQueryHasNextPage && !answersQueryIsFetching) {
      answersQueryFetchNextPage();
    }
  }, [answersQueryHasNextPage, answersQueryIsFetching]);

  useEffect(() => {
    let worker: Worker;
    if (
      !isFetchingFormSubmissions &&
      !bulkExportPDFMutation.isLoading &&
      allFormSubmissions.length > 0 &&
      !downloadedRef.current
    ) {
      startTimeRef.current = performance.now();
      downloadedRef.current = true;
      worker = new Worker(
        new URL("#src/workers/form-submission-pdf.worker.ts", import.meta.url)
      );
      bulkExportPDFMutation.mutate({
        formSubmissions: allFormSubmissions ?? [],
        worker,
        onProgress: (pdfProgress, downloadUrl) => {
          setDownloadUrl(downloadUrl);
          setPdfProgress(pdfProgress);
        },
      });
    }
  }, [
    isFetchingFormSubmissions,
    allFormSubmissions,
    bulkExportPDFMutation.isLoading,
  ]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl]);

  const progress = useMemo(() => {
    if (bulkExportPDFMutation.isLoading || bulkExportPDFMutation.isError)
      return pdfProgress;
    if (initialFormSubmissionsQuery.isLoading) return 5;
    if (
      initialFormSubmissionsQuery.data?.total_entries &&
      initialFormSubmissionsQuery.data.total_entries > PAGE_SIZE
    ) {
      const numberDownloaded = remainingFormSubmissionsQueries
        .map((q) => (!q.isLoading ? 1 : 0) as number)
        .reduce((a, b) => a + b, 0);
      return (
        ((1 + numberDownloaded) /
          (1 + remainingFormSubmissionsQueries.length)) *
        100
      );
    }
    if (downloadUrl) return 100;
    return 0;
  }, [
    initialFormSubmissionsQuery.data?.total_entries,
    remainingFormSubmissionsQueries,
    totalNumberItems,
    downloadUrl,
    pdfProgress,
    bulkExportPDFMutation.isLoading,
    bulkExportPDFMutation.isError,
    bulkExportPDFMutation.isSuccess,
  ]);

  useEffect(() => {
    if (bulkExportPDFMutation.isSuccess) {
      const endTime = performance.now();
      const duration = endTime - startTimeRef.current; // eslint-disable-next-line no-console
      // eslint-disable-next-line no-console
      console.info(`Time taken: ${duration / 1000} seconds`);
    }
  }, [bulkExportPDFMutation.isSuccess]);

  useEffect(() => {
    if (bulkExportPDFMutation.isError) {
      toast.push({
        intent: "error",
        description:
          "Something went wrong, please close this page and try again.",
      });
    }
  }, [bulkExportPDFMutation.isError]);

  return (
    <Titled title={"Downloading your form submissions"}>
      <Page
        title={"Downloading your form submissions"}
        category="Form Submissions"
        breadcrumbs={breadcrumbs}
        actionRow={[]}
        renderMeta={({ MetaSegments }) => <MetaSegments values={[]} />}
        shouldCenterContent={true}
      >
        <div className={cx("container")}>
          <div className={cx("contents")}>
            <LoadingState
              label={
                isFetchingFormSubmissions
                  ? "Downloading your form submissions..."
                  : bulkExportPDFMutation.isLoading
                    ? "Generating PDFs"
                    : "Completed"
              }
              size="normal"
              indicator={<Brandmark isAnimated={!downloadUrl} />}
            />
            <ProgressBar
              intent={
                bulkExportPDFMutation.isError
                  ? "error"
                  : progress >= 100
                    ? "success"
                    : progress >= 50
                      ? "warning"
                      : "info"
              }
              value={progress}
              max={100}
              showAnimation={progress > 0}
            />
            <div className={cx("progress-text-container")}>
              <div className={cx("pdfProgress-text")}>
                You can continue to use the app in your previous tab while the
                PDFs are being generated. Please keep this page open until the
                process is complete. This can take a long amount of time
                depending on number of form submissions and images. Thank you
                for your patience.
              </div>
              <div className={cx("pdfProgress-text")}>
                Progress: <span className={cx("bold")}>{progress}%</span>
              </div>
              {initialFormSubmissionsQuery.data && (
                <div className={cx("pdfProgress-text")}>
                  Exporting{" "}
                  <span className={cx("bold")}>
                    {initialFormSubmissionsQuery.data.total_entries.toLocaleString()}
                  </span>{" "}
                  form submission
                  {initialFormSubmissionsQuery.data.total_entries > 1
                    ? "s"
                    : ""}
                </div>
              )}
            </div>
            <div className={cx("button-container")}>
              <Button
                variant="error-outline"
                onClick={() => window.close()}
              >
                {bulkExportPDFMutation.isSuccess ||
                bulkExportPDFMutation.isError
                  ? "Close"
                  : "Cancel"}
              </Button>
              {downloadUrl && (
                <Button
                  variant="primary"
                  onClick={() => {
                    const a = document.createElement("a");
                    a.href = downloadUrl;
                    a.download = `${templateQuery.data?.name}: Submissions.zip`;
                    a.click();
                  }}
                >
                  Download
                </Button>
              )}
            </div>
          </div>
        </div>
      </Page>
    </Titled>
  );
};
